<template>
  <v-container
    class="d-flex align-center justify-center"
    style="min-height: 60vh"
  >
    <v-card max-width="700" class="pa-8 text-center">
      <!-- Loading State -->
      <div v-if="isLoading">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="mb-4"
        ></v-progress-circular>
        <h2 class="text-h5 mb-2">Loading waitlist offer...</h2>
        <p class="text-body-1">
          Please wait while we retrieve your offer details.
        </p>
      </div>

      <!-- Offer State - Show offer with countdown and buttons -->
      <div v-else-if="showOffer && classInstance">
        <v-icon
          icon="mdi-clock-check"
          color="success"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h4 mb-4">Waitlist Offer Available!</h2>

        <!-- Class Details -->
        <v-card variant="outlined" class="mb-6 pa-4 text-left">
          <h3 class="text-h6 mb-2">
            {{ classInstance.classInstance.className }}
          </h3>
          <p class="text-body-2 mb-1" v-if="firstSessionDatetime">
            <strong>First Session:</strong>
            {{ formatDateNY(firstSessionDatetime) }}
            @
            {{ formatTimeNY(firstSessionDatetime) }}
          </p>
          <p
            class="text-body-2 mb-1"
            v-if="classInstance.classInstance.locationName"
          >
            <strong>Location:</strong>
            {{ classInstance.classInstance.locationName }}
          </p>
          <p
            class="text-body-2"
            v-if="classInstance.classInstance.availability?.priceCents"
          >
            <strong>Price:</strong>
            {{
              formatRawCost(classInstance.classInstance.availability.priceCents)
            }}
          </p>
        </v-card>

        <!-- Countdown Timer -->
        <v-alert
          :color="waitlistCountdown.isUrgent.value ? 'error' : 'warning'"
          :icon="
            waitlistCountdown.isUrgent.value ? 'mdi-alert' : 'mdi-clock-outline'
          "
          variant="tonal"
          class="mb-6"
          prominent
        >
          <div class="text-h6 mb-1">
            <template v-if="waitlistCountdown.isExpired.value">
              This offer has expired
            </template>
            <template v-else>
              Time Remaining: {{ waitlistCountdown.timeRemaining.value }}
            </template>
          </div>
          <div class="text-body-2">
            <template v-if="!waitlistCountdown.isExpired.value">
              <template v-if="waitlistCountdown.isUrgent.value">
                Act quickly! This offer will expire soon.
              </template>
              <template v-else>
                Accept or decline this offer before it expires.
              </template>
            </template>
          </div>
        </v-alert>

        <!-- Action Buttons -->
        <div class="d-flex flex-column flex-sm-row ga-6 justify-center">
          <v-btn
            color="success"
            size="large"
            :loading="isProcessing"
            :disabled="waitlistCountdown.isExpired.value"
            @click="acceptOffer"
            prepend-icon="mdi-check"
          >
            {{ isPaidClass ? "Add to Cart" : "Accept Offer" }}
          </v-btn>
          <v-btn
            color="error"
            size="large"
            variant="outlined"
            :loading="isDeclining"
            :disabled="waitlistCountdown.isExpired.value"
            @click="declineOffer"
            prepend-icon="mdi-close"
          >
            Decline Offer
          </v-btn>
        </div>
      </div>

      <!-- Success State - Enrolled -->
      <div v-else-if="isSuccess && !requiresPayment">
        <v-icon
          icon="mdi-check-circle"
          color="success"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h4 mb-4">Successfully Enrolled!</h2>
        <p class="text-body-1 mb-6">
          You have been successfully enrolled in the class. You should receive a
          confirmation email shortly.
        </p>
        <div class="d-flex flex-column flex-sm-row ga-6 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'home' }">
            Return to Homepage
          </v-btn>
        </div>
      </div>

      <!-- Success State - Requires Payment -->
      <div v-else-if="isSuccess && requiresPayment">
        <v-icon icon="mdi-cart" color="primary" size="64" class="mb-4"></v-icon>
        <h2 class="text-h4 mb-4">Added to Cart!</h2>
        <p class="text-body-1 mb-6">
          Your spot has been reserved! Please complete your purchase to finalize
          your enrollment.
        </p>
        <v-alert
          :color="purchaseHoldCountdown.isUrgent.value ? 'error' : 'warning'"
          :icon="
            purchaseHoldCountdown.isUrgent.value
              ? 'mdi-alert'
              : 'mdi-clock-outline'
          "
          variant="tonal"
          class="mb-6"
          prominent
        >
          <div class="text-h6 mb-1">
            <template v-if="purchaseHoldCountdown.isExpired.value">
              This purchase hold has expired
            </template>
            <template v-else>
              Time Remaining: {{ purchaseHoldCountdown.timeRemaining.value }}
            </template>
          </div>
          <div class="text-body-2">
            <template v-if="!purchaseHoldCountdown.isExpired.value">
              <template v-if="purchaseHoldCountdown.isUrgent.value">
                Act quickly! This purchase hold will expire soon.
              </template>
              <template v-else>
                Complete purchase before hold expires.
              </template>
            </template>
          </div>
        </v-alert>
        <div class="d-flex flex-column flex-sm-row ga-6 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'portal-checkout' }">
            Go to Checkout
          </v-btn>
          <v-btn
            color="secondary"
            size="large"
            variant="outlined"
            :to="{ name: 'home' }"
          >
            Continue Shopping
          </v-btn>
        </div>
      </div>

      <!-- Declined State -->
      <div v-else-if="isDeclined">
        <v-icon
          icon="mdi-close-circle"
          color="info"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h4 mb-4">Offer Declined</h2>
        <p class="text-body-1 mb-6">
          You have declined this waitlist offer. You can view other available
          classes on the homepage.
        </p>
        <div class="d-flex flex-column flex-sm-row gap-3 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'home' }">
            Return to Homepage
          </v-btn>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError">
        <v-icon
          icon="mdi-alert-circle"
          color="error"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h5 mb-4">Unable to Process Request</h2>
        <p class="text-body-1 mb-2">{{ errorMessage }}</p>
        <p class="text-body-2 text-grey mb-6">
          Please contact support if you continue to experience issues.
        </p>
        <div class="d-flex flex-column flex-sm-row ga-6 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'home' }">
            Return to Homepage
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { useRoute } from "vue-router";
import { useUser } from "@/composables/useUser";
import {
  CombinedClassType,
  getCalendarSessions,
  legacyIdToProgramType,
  useClassManager,
} from "@/composables/classes/useClassManager";
import { useUtility } from "@/composables/useUtility";
import { useCountdown } from "@/composables/useCountdown";
import { useClassUnifiedService } from "../../../cbi-generator/frontend/composables/useClassUnifiedFrontendService";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import type { ClassEligibility } from "../../../cbi-generator/generated/dto/ClassEligibility";
import { ClassEligibilityStatus } from "../../../cbi-generator/generated/dto/ClassEligibilityStatus";
import { SignupTypeEnum as UnifiedSignupTypeEnum } from "../../../cbi-generator/generated/dto/SignupTypeEnum";

const route = useRoute();
const user = useUser();
const memberOrder = inject<MemberOrderSession | null>(
  memberOrderSessionKey,
  null,
);
const { formatRawCost, formatDateNY, formatTimeNY } = useUtility();

const isLoading = ref(true);
const isSuccess = ref(false);
const hasError = ref(false);
const requiresPayment = ref(false);
const isDeclined = ref(false);
const showOffer = ref(false);
const isProcessing = ref(false);
const isDeclining = ref(false);
const errorMessage = ref("Unable to process your waitlist acceptance.");
const classInstance = ref<CombinedClassType | null>(null);
const existingSignup = ref<ClassEligibility | null>(null);
const firstSessionDatetime = computed(() =>
  classInstance.value
    ? getCalendarSessions(classInstance.value.classInstance)[0]?.sessionDatetime
    : undefined,
);

// Create a minimal class manager just for the enrollment update
const manager = useClassManager({
  selectablePeople: ref([
    { title: "Me", value: user.currentPersonRef.value?.personId || 0 },
  ]),
  selectedPerson: user.currentPersonRef.value?.personId,
  memberOrder: memberOrder ?? undefined,
});

const waitlistCountdown = useCountdown();

const purchaseHoldCountdown = useCountdown();

// Check if this is a paid class
const isPaidClass = computed(() => {
  return (
    classInstance.value?.classInstance.availability?.waitlistedEnrollAction ===
    UnifiedSignupTypeEnum.PURCHASE_HOLD
  );
});

const loadWaitlistOffer = async () => {
  try {
    // Get parameters from URL
    const instanceId = parseInt(route.query.instanceId as string);
    const programId = parseInt(route.query.programId as string);

    if (!instanceId || isNaN(instanceId)) {
      hasError.value = true;
      errorMessage.value = "Invalid or missing class instance ID.";
      isLoading.value = false;
      return;
    }

    const programType = legacyIdToProgramType(programId);
    if (!programId || isNaN(programId) || !programType) {
      hasError.value = true;
      errorMessage.value = "Invalid or missing program ID.";
      isLoading.value = false;
      return;
    }

    if (!user.currentPersonRef.value?.personId) {
      hasError.value = true;
      errorMessage.value =
        "You must be logged in to accept a waitlist position.";
      isLoading.value = false;
      return;
    }

    const personId = user.currentPersonRef.value.personId;
    const searchFrom = new Date();
    searchFrom.setDate(searchFrom.getDate() - 1);
    const found = await useClassUnifiedService().memberClassesMember({
      includeAvailability: true,
      includeCancelled: false,
      startDate: searchFrom,
      endDate: null,
      typeId: null,
      programType,
      classId: instanceId,
      signupId: null,
      people: [{ personId }],
    });
    const foundClass = found
      .filter((c) => c.classId === instanceId && c.programType === programType)
      .map((c) => ({
        classInstance: c,
        personEligibility: c.eligibility ?? [],
      }))[0];

    if (!foundClass) {
      hasError.value = true;
      errorMessage.value =
        "Class not found or you are not eligible for this class.";
      isLoading.value = false;
      return;
    }

    classInstance.value = foundClass;

    // Find the person's existing signup
    const signup = foundClass.personEligibility.find(
      (a) => a.personId.personId == personId,
    );

    if (!signup || signup.signupType !== UnifiedSignupTypeEnum.WAITLISTED) {
      hasError.value = true;
      errorMessage.value =
        "You do not have a pending waitlist offer for this class.";
      isLoading.value = false;
      return;
    }

    if (signup.status !== ClassEligibilityStatus.WAITLISTED_PENDING) {
      hasError.value = true;
      errorMessage.value = "This waitlist offer is no longer available.";
      isLoading.value = false;
      return;
    }

    existingSignup.value = signup;
    waitlistCountdown.reset(signup.offerExpDatetime ?? undefined);
    showOffer.value = true;
  } catch (error: any) {
    console.error("Error loading waitlist offer:", error);
    hasError.value = true;

    // Try to extract a more specific error message
    if (error?.response?.data?.snackBarError) {
      errorMessage.value = error.response.data.snackBarError;
    } else if (error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else if (error?.message) {
      errorMessage.value = error.message;
    }
  } finally {
    isLoading.value = false;
  }
};

const acceptOffer = async () => {
  if (!classInstance.value || !user.currentPersonRef.value?.personId) return;

  isProcessing.value = true;
  try {
    // Call updateEnrollment to accept the waitlist offer
    requiresPayment.value = await manager.updateEnrollment(
      classInstance.value.classInstance.availability?.waitlistedEnrollAction,
      user.currentPersonRef.value.personId,
      classInstance.value.classInstance,
    );

    const signupShopItem = memberOrder?.order.value?.lineItems?.find(
      (a) =>
        !!a.classSignup &&
        a.classSignup?.signupId == existingSignup.value?.signupId,
    );

    if (signupShopItem != null) {
      purchaseHoldCountdown.reset(signupShopItem.expiresOn ?? undefined);
    }

    showOffer.value = false;
    isSuccess.value = true;
  } catch (error: any) {
    console.error("Error accepting waitlist offer:", error);
    hasError.value = true;
    showOffer.value = false;

    // Try to extract a more specific error message
    if (error?.response?.data?.snackBarError) {
      errorMessage.value = error.response.data.snackBarError;
    } else if (error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else if (error?.message) {
      errorMessage.value = error.message;
    } else {
      errorMessage.value = "Unable to accept waitlist offer. Please try again.";
    }
  } finally {
    isProcessing.value = false;
  }
};

const declineOffer = async () => {
  if (!classInstance.value || !user.currentPersonRef.value?.personId) return;

  isDeclining.value = true;
  try {
    // Call updateEnrollment with UNENROLLED to decline the offer
    await manager.updateEnrollment(
      UnifiedSignupTypeEnum.UNENROLLED,
      user.currentPersonRef.value.personId,
      classInstance.value.classInstance,
    );

    showOffer.value = false;
    isDeclined.value = true;
  } catch (error: any) {
    console.error("Error declining waitlist offer:", error);
    hasError.value = true;
    showOffer.value = false;

    // Try to extract a more specific error message
    if (error?.response?.data?.snackBarError) {
      errorMessage.value = error.response.data.snackBarError;
    } else if (error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else if (error?.message) {
      errorMessage.value = error.message;
    } else {
      errorMessage.value =
        "Unable to decline waitlist offer. Please try again.";
    }
  } finally {
    isDeclining.value = false;
  }
};

onMounted(async () => {
  await user.awaitFetch();
  loadWaitlistOffer();
});
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
